import React from 'react'
import { withTheme } from '@emotion/react'
import {
    StyledButton,
    StyledContainer,
    StyledContentContainer,
    StyledText,
    StyledInput,
    StyledForm,
    StyledIcon,
    StyledFormContainer,
    StyledLabel,
    StyledTextLink,
} from '../../banners/NewsletterSignup/NewsletterSignup.styled'
import { Theme } from '../../__styling/themes'
import { PerthNowMark } from '../../logos/PerthNowMark/PerthNowMark'
import { LogoSevenMark } from '../../logos/LogoSevenMark/LogoSevenMark'
import { TheWestMark } from '../../logos/TheWestMark/TheWestMark'
import { themedValue } from '../../__styling/themed-value'
import { colors } from '../../__styling/settings/colors'
import { ThemeMargins } from '../../__styling/settings/metrics'

export interface InternalNewsletterSignupProps {
    text: string
    buttonLabel: string
    onEvent: (event: any) => void
    api: string
    verticalSpacing?: keyof ThemeMargins
}

interface State {
    email: string
    honeypot: string
    error: boolean
    success: boolean
    isSubmitting: boolean
}

class InternalNewsletterSignup extends React.Component<
    InternalNewsletterSignupProps & { theme: Theme },
    State
> {
    static displayName = 'InternalNewsletterSignup'

    state = {
        email: '',
        honeypot: '',
        error: false,
        success: false,
        isSubmitting: false,
    }

    handleSubmit = async (e: React.FormEvent<any>) => {
        e.preventDefault()
        this.setState({ isSubmitting: true })

        try {
            const api = this.props.api

            const response = await fetch(
                `${api}/subscribe?email=${this.state.email}&address=${this.state.honeypot}`,
                { method: 'POST' },
            )

            if (response.ok) {
                this.setState({ success: true, isSubmitting: false }, () => {
                    this.props.onEvent({
                        type: 'newsletterSignup.success',
                        originator: 'NewsletterSignupBanner',
                        timeStamp: Date.now(),
                        payload: {
                            creativeName: 'Default',
                            buttonText: this.props.buttonLabel,
                            newsletterDatabase: 'daily_news',
                        },
                    })
                })
            } else {
                this.setState({ isSubmitting: false, error: true }, () => {
                    this.props.onEvent({
                        type: 'newsletterSignup.fail',
                        originator: 'NewsletterSignupBanner',
                        timeStamp: Date.now(),
                        payload: {
                            creativeName: 'Default',
                            buttonText: this.props.buttonLabel,
                            newsletterDatabase: 'daily_news',
                        },
                    })
                })

                throw new Error(
                    `NewsletterSignupBanner submission error: ${response.status} ${response.statusText}`,
                )
            }
        } catch (error) {
            console.error(error.message)
        }
    }

    handleFocus = () => {
        this.props.onEvent({
            type: 'newsletterSignup.interact',
            originator: 'NewsletterSignupBanner',
            timeStamp: Date.now(),
            payload: {
                creativeName: 'Default',
                buttonText: this.props.buttonLabel,
                newsletterDatabase: 'daily_news',
            },
        })
    }

    renderMarkIcon(theme: Theme) {
        switch (theme.kind) {
            case 'perthnow':
                return <PerthNowMark />
            case 'thewest':
                return <TheWestMark fill={colors.white} />
            case 'sevennews':
                return <LogoSevenMark />
            default:
                return <TheWestMark />
        }
    }

    componentDidMount = () => {
        this.props.onEvent({
            type: 'newsletterSignup.available',
            originator: 'NewsletterSignupBanner',
            timeStamp: Date.now(),
            payload: {
                creativeName: 'Default',
                buttonText: this.props.buttonLabel,
                newsletterDatabase: 'daily_news',
            },
        })
    }

    render() {
        const { isSubmitting, success, email, error, honeypot } = this.state
        const theme = this.props.theme

        const subscribeUrl = themedValue(theme, {
            perthnow: 'https://www.perthnow.com.au/newslettersubscribe',
            fallback: '/newslettersubscribe',
        })

        const contactUrl = themedValue(theme, {
            perthnow: 'https://www.perthnow.com.au/contact',
            fallback: 'https://thewest.com.au/contact',
        })

        return (
            <StyledContainer verticalSpacing={this.props.verticalSpacing}>
                <StyledIcon>{this.renderMarkIcon(theme)}</StyledIcon>
                <StyledContentContainer>
                    <StyledFormContainer>
                        {success && !isSubmitting && (
                            <StyledText>Thank you for subscribing!</StyledText>
                        )}

                        {isSubmitting && <StyledText>Submitting...</StyledText>}

                        {!success && error && (
                            <StyledText>
                                Something went wrong, please{' '}
                                <StyledTextLink href={subscribeUrl}>
                                    try again
                                </StyledTextLink>{' '}
                                or{' '}
                                <StyledTextLink href={contactUrl}>
                                    contact us
                                </StyledTextLink>
                            </StyledText>
                        )}

                        {!success && !error && !isSubmitting && (
                            <React.Fragment>
                                <StyledText>{this.props.text}</StyledText>
                                <StyledForm
                                    method="POST"
                                    onSubmit={this.handleSubmit}
                                >
                                    <StyledLabel htmlFor="newsletterSignup-emailAddress">
                                        Your email address:
                                    </StyledLabel>
                                    <StyledInput
                                        name="email"
                                        id="newsletterSignup-email"
                                        value={email}
                                        type="email"
                                        required
                                        placeholder="Your email address"
                                        onFocus={this.handleFocus}
                                        onChange={(e) =>
                                            this.setState({
                                                email: e.currentTarget.value,
                                            })
                                        }
                                    />

                                    {/* Honeypot field, will be rejected by API if a value is entered */}
                                    <StyledInput
                                        hidden
                                        name="address"
                                        id="newsletterSignup-address"
                                        value={honeypot}
                                        type="text"
                                        onChange={(e) =>
                                            this.setState({
                                                honeypot: e.currentTarget.value,
                                            })
                                        }
                                    />

                                    <StyledButton
                                        type="submit"
                                        disabled={isSubmitting}
                                    >
                                        {this.props.buttonLabel}
                                    </StyledButton>
                                </StyledForm>
                            </React.Fragment>
                        )}
                    </StyledFormContainer>
                </StyledContentContainer>
            </StyledContainer>
        )
    }
}

export const NewsletterSignup = withTheme(InternalNewsletterSignup)
